import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { AgeCheckModalConfirmDynamic } from '@/features/age-check-modal-confirm/dynamic';
import { LoginConfirmationModalDynamic } from '@/features/login-confirmation-modal';
import { useIsLoginConfirmationDone } from '@/features/login-confirmation-modal/model/hook';
import { FillOutModalDynamic } from '@/features/fill-out-modal/dynamic';
import { GoldCardDynamic } from '@/features/gold-card/dynamic';
import { LastChanceDepositDynamic } from '@/features/last-chance-deposit/dynamic';
import { OnboardingDynamic } from '@/features/onboarding/dynamic';
import { KycVerificationDynamic } from '@/features/kyc-verification-modal/dynamic';
import { KycResultModal, KycResultStatus } from '@/features/kyc-verification-modal/steps';
import { useNotifications } from '@/shared/providers/notifications-provider/notifications-context';
import { useGoldCard, useOnboardingPopup } from '@/shared/providers/notifications-provider/store';
import { useUserPromoPopup, useIsUserLoading, useIsAuth, useUserKycVerified, useHasPhProfile } from '@/shared/providers/user-provider/use-user';
import { useParams, usePathname, useSearchParams, useRouter } from 'next/navigation';
import { AutoViewModalHandler } from '../auto-view-modal-handler';
import { brandIsPh, brandIsPh2 } from '@/shared/config/brand-by-target.mjs';
import { usePhSettings } from '@/features/auth-management/model/hooks/use-ph-settings';

interface AuthModalsProps {
  onClick?: () => void;
}

const AuthModals = ({ onClick }: AuthModalsProps) => {
  const isUserLoading = useIsUserLoading();
  const isAuth = useIsAuth();
  const kycVerified = useUserKycVerified();
  const hasPhProfile = useHasPhProfile();
  const promoPopup = useUserPromoPopup();
  const goldCard = useGoldCard();
  const onboardingPopup = useOnboardingPopup();
  const { notifications } = useNotifications();
  const params = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [sessionFlags, setSessionFlags] = useState({
    isReady: false,
    hasAgeCheck: false,
    hasGoldCard: false,
    kycVerified: null,
  });
  const isLoginConfirmationDone = useIsLoginConfirmationDone();
  const { fillOut } = usePhSettings();

  const modal = searchParams.get('modal');
  const isModalKyc = modal === 'kyc';
  const isInfoPage = pathname.includes('info');
  const isGamePage = Boolean(params.categories && params.titleGame);

  const shouldLoadAutoViewModal =
    isUserLoading === false && !isAuth;
  const shouldLoadKyc = isAuth && isModalKyc && !kycVerified;
  const kycResultStatus: KycResultStatus | null =
    isAuth &&
    !shouldLoadKyc &&
    notifications?.kycVerified != null &&
    sessionFlags.kycVerified !== String(notifications.kycVerified)
      ? (notifications.kycVerified ? 'completed' : 'under_review')
      : null;
  const shouldLoadFillOut =
    isAuth && modal === 'fillOut' && !hasPhProfile && fillOut;
  const shouldLoadGoldCard =
    isAuth &&
    (sessionFlags.hasGoldCard || Boolean(goldCard));
  const shouldLoadAgeCheck =
    sessionFlags.isReady &&
    isUserLoading === false &&
    isAuth === false &&
    brandIsPh &&
    !brandIsPh2 &&
    !sessionFlags.hasAgeCheck &&
    !isInfoPage;
  const shouldLoadLoginConfirmation =
    brandIsPh &&
    !brandIsPh2 &&
    isUserLoading === false &&
    isAuth === true &&
    !isLoginConfirmationDone;
  const shouldLoadLastChanceDeposit =
    isAuth &&
    Boolean(promoPopup) &&
    !shouldLoadLoginConfirmation &&
    !isGamePage;
  const shouldLoadOnboarding =
    brandIsPh &&
    isAuth &&
    !shouldLoadLoginConfirmation &&
    Boolean(onboardingPopup);

  const handleCloseKycResult = () => {
    setSessionFlags((prev: any) => ({ ...prev, kycVerified: String(notifications?.kycVerified) }));
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('modal');
    router.push(`?${newSearchParams.toString()}`, { scroll: false });
  };

  useEffect(() => {
    let hasAgeCheck = Boolean(sessionStorage.getItem('isAgeCheck'));
    if (!hasAgeCheck && Cookies.get('sessionId')) {
      sessionStorage.setItem('isAgeCheck', 'true');
      hasAgeCheck = true;
    }
    setSessionFlags({
      isReady: true,
      hasAgeCheck,
      hasGoldCard: Boolean(sessionStorage.getItem('gold-card')),
      kycVerified: sessionStorage.getItem('notification-kyc-verified'),
    });
  }, []);

  return (
    <>
      {shouldLoadAutoViewModal && <AutoViewModalHandler />}
      {shouldLoadKyc && <KycVerificationDynamic />}
      {kycResultStatus && (
        <KycResultModal
          status={kycResultStatus}
          open
          onClose={handleCloseKycResult}
        />
      )}
      {shouldLoadFillOut && <FillOutModalDynamic />}
      {shouldLoadGoldCard && <GoldCardDynamic />}
      {shouldLoadLastChanceDeposit && (
        <LastChanceDepositDynamic onClick={onClick} />
      )}
      {shouldLoadOnboarding && <OnboardingDynamic />}
      {shouldLoadAgeCheck && <AgeCheckModalConfirmDynamic />}
      {shouldLoadLoginConfirmation && <LoginConfirmationModalDynamic />}
    </>
  );
};

export { AuthModals };
